import { useState, useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { analytics } from "MVVM/Analytics";
import { ISearchItem, SearchBar } from "../SearchBar";
import { IPlayer } from "MVVM/@types";
import { MatchHistorySearchViewModel } from "./ViewModel";

const MatchHistorySearch = ({
  locationIdentifier,
  placeholder,
  width,
  backgroundColor,
  hoverColor,
  onPlayerSelect,
}: {
  locationIdentifier: string;
  placeholder: string;
  width?: string;
  backgroundColor?: string;
  hoverColor?: string;
  onPlayerSelect?: (gameId: string) => void;
}) => {
  const [users, setUsers] = useState<IPlayer[]>([]);
  const history = useHistory();
  const debouncedSearchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { playerSearchQuery, loading } = MatchHistorySearchViewModel();
  const transformUserToSearchItem = (player: IPlayer): ISearchItem => ({
    id: player.gameId,
    name: player.displayName,
    image: player.avatar,
    gameId: player.gameId,
  });

  const searchPlayers = useCallback(
    async (query: string) => {
      if (query.trim() !== "") {
        try {
          const response = await playerSearchQuery({
            variables: {
              count: 10,
              query,
            },
          });

          setUsers(response.data.playerSearchNew);
        } catch (_) {
          setUsers([]);
        }
      } else {
        setUsers([]);
      }
    },
    [playerSearchQuery, setUsers],
  );

  const debouncedSearch = useCallback(
    (query: string) => {
      if (debouncedSearchTimeoutRef.current)
        clearTimeout(debouncedSearchTimeoutRef.current);

      debouncedSearchTimeoutRef.current = setTimeout(() => {
        searchPlayers(query);
      }, 300);
    },
    [searchPlayers],
  );

  const onFocus = () => {
    analytics.track("Search - Focused", {
      location: "match_history",
      locationIdentifier,
    });
  };

  useEffect(() => {
    return () => {
      if (debouncedSearchTimeoutRef.current) {
        clearTimeout(debouncedSearchTimeoutRef.current);
      }
    };
  }, []);

  const onSelect = (item: ISearchItem) => {
    const gameId = item.gameId || item.steam?.steamid;
    if (!gameId) {
      return;
    }

    analytics.track("Button - Click", {
      location: "matchHistory",
      locationIdentifier,
      source: onPlayerSelect ? "iframe" : "website",
    });

    if (onPlayerSelect) {
      onPlayerSelect(gameId);
    } else {
      history.push(`/match-history?playerSearchIdentifier=${gameId}`);
    }
  };

  return (
    <>
      <SearchBar
        items={users.map(transformUserToSearchItem)}
        loading={loading}
        onSelect={onSelect}
        onFocus={onFocus}
        debouncedSearch={debouncedSearch}
        placeholder={placeholder}
        width={locationIdentifier === "matchHistoryWidget" ? "100%" : width}
        backgroundColor={backgroundColor}
        hoverColor={hoverColor}
        locationIdentifier={locationIdentifier}
      />
    </>
  );
};

export { MatchHistorySearch };
